import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import Link from 'flarum/common/components/Link';
import Dropdown from 'flarum/common/components/Dropdown';
import Avatar from 'flarum/common/components/Avatar';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import tagsLabel from 'ext:flarum/tags/common/helpers/tagsLabel';
import sortTags from 'ext:flarum/tags/common/utils/sortTags';
/**
 * A discussion page that reads as an article: a large title, then the date and
 * tags, then the first post at full width, then the replies as "Comments".
 *
 * Before this, clicking a post in the feed landed on a coloured hero band, an
 * avatar column and a "Leave a reply" stub — a forum thread. Everything below
 * the header is CSS (forum.less, `.LrArticle`); the only markup this adds is
 * the header itself. The PostStream is untouched.
 *
 * 🛑 NOT on Admin-tagged discussions. Those are real working threads — the
 * changelog, the spam list, the Postgres notes — with several comments each.
 * Flattening them into articles would wreck the one place the forum is used as
 * a forum.
 *
 * Both extenders swallow their own errors: the cost of being wrong is a page
 * that looks like a forum, and the cost of throwing is a page that renders
 * EMPTY. If this file is edited, check every helper it calls still exists —
 * babel does not resolve names, so a deleted helper builds fine and the
 * try/catch turns the ReferenceError into a silent fall-back (it happened once,
 * 2026-09-27).
 */
export default function articlePost() {
  override(DiscussionPage.prototype, 'hero', function (original) {
    try {
      const discussion = this.discussion;
      if (!isArticle(discussion)) return original();
      const rawTags = discussion.tags?.();
      const tags = Array.isArray(rawTags) ? sortTags(rawTags.filter(Boolean)) : [];
      const author = discussion.user?.();
      const created = discussion.createdAt?.() || null;

      // The discussion controls menu, which used to live in the sidebar column
      // this page no longer has: Follow, Rename, Lock, Delete… as a small
      // three-dot at the right end of the byline row. Reply is dropped — the comment box at the
      // bottom is how you reply — and so is the menu itself when nothing else
      // is left (a guest, a member who cannot edit).
      const list = DiscussionControls.controls(discussion, this);
      list.remove('reply');
      const controls = list.toArray().filter(c => !String(c?.itemName ?? '').endsWith('Separator'));
      const comments = Math.max(0, (discussion.commentCount?.() ?? 1) - 1);
      return m("header", {
        className: "LrArticle-header",
        oncreate: v => setCount(v.dom, comments),
        onupdate: v => setCount(v.dom, comments)
      }, m("div", {
        className: "container"
      }, m("h4", {
        className: "App-titleControl App-titleControl--text LrArticle-barTitle"
      }, m(Link, {
        href: app.route('index')
      }, app.translator.trans('linkrobins-lr-home.forum.article.section'))), m("div", {
        className: "LrArticle-titleRow"
      }, m("h1", {
        className: "LrArticle-title"
      }, discussion.title())), m("div", {
        className: "LrArticle-byline"
      }, author ? m("span", {
        className: "LrArticle-author"
      }, m(Avatar, {
        user: author,
        className: "LrArticle-avatar"
      }), m(Link, {
        href: userHref(author)
      }, author.displayName())) : null, created ? m("time", {
        datetime: created.toISOString()
      }, longDate(created)) : null, tags.length ? m("span", {
        className: "LrArticle-tags"
      }, tagsLabel(tags)) : null, controls.length ? m(Dropdown, {
        icon: "fas fa-ellipsis-v",
        className: "LrArticle-controls",
        menuClassName: "Dropdown-menu--right",
        buttonClassName: "Button Button--icon Button--flat",
        accessibleToggleLabel: app.translator.trans('core.forum.discussion_controls.toggle_dropdown_accessible_label')
      }, list.toArray()) : null)));
    } catch (e) {
      return original();
    }
  });

  // No scrubber (a blog article has no post-stream timeline) and no Reply
  // button: the sidebar's controls moved beside the title (see hero above).
  extend(DiscussionPage.prototype, 'sidebarItems', function (items) {
    try {
      if (isArticle(this.discussion)) {
        items.remove('scrubber');
        items.remove('controls');
      }
    } catch (e) {
      /* keep the scrubber if anything above is missing */
    }
  });

  // The root class everything else hangs off, added to the vnode core just
  // returned rather than by replacing view().
  extend(DiscussionPage.prototype, 'view', function (vdom) {
    try {
      if (!isArticle(this.discussion) || !vdom?.attrs) return;
      vdom.attrs.className = `${vdom.attrs.className || ''} LrArticle`.trim();
    } catch (e) {
      /* layout only — never take the page down for a class name */
    }
  });
}

/** Publishes the comment count to CSS (the "Comments (N)" heading). */
function setCount(dom, n) {
  try {
    dom.closest('.DiscussionPage')?.style.setProperty('--lr-comments', `"${n}"`);
  } catch (e) {
    /* heading just reads "Comments" */
  }
}

/** A discussion that should read as an article: loaded, and not an Admin one. */
function isArticle(discussion) {
  if (!discussion || typeof discussion.title !== 'function') return false;
  const tags = discussion.tags?.() || [];
  return !(Array.isArray(tags) ? tags : []).some(t => t?.slug?.() === 'admin');
}

/** The author's profile, with a literal fallback: a throw here is a blank page. */
function userHref(user) {
  try {
    return app.route.user(user);
  } catch (e) {
    return `/u/${user.slug ? user.slug() : user.username()}`;
  }
}

/** "October 26, 2026" — an article carries a date, not "3 days ago". */
function longDate(d) {
  try {
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (e) {
    return '';
  }
}